import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronDown, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import {
  useGetLeavesForSupervisorQuery,
  useRejectLeaveAsSupervisorMutation,
  useForwardLeaveToManagerMutation,
} from "../../slices/leaveApiSlice";
import DateRangePicker from "../../components/DateRangePicker/DateRangePicker";
import CustomSelect from "../../components/CustomSelect/CustomSelect";

export default function SupervisorEmployeeLeaveRequests() {
  const navigate = useNavigate();

  // Get user info from Redux store (supervisor info)
  const { userInfo } = useSelector((state) => state.auth);

  // Enhanced supervisor info extraction with multiple fallbacks
  const supervisorId = userInfo?.userId || userInfo?.id || userInfo?._id;
  const supervisorEmail =
    userInfo?.email ||
    userInfo?.Email ||
    userInfo?.supervisorEmail ||
    userInfo?.username;
  const supervisorName =
    userInfo?.name ||
    userInfo?.userName ||
    userInfo?.displayName ||
    "Supervisor";
  const divisionId = userInfo?.divisionId;

  // RTK Query hooks - pass divisionId to the query
  const {
    data: leaveDataResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetLeavesForSupervisorQuery(divisionId || "");

  const [rejectLeave] = useRejectLeaveAsSupervisorMutation();
  const [forwardLeave] = useForwardLeaveToManagerMutation();

  useEffect(() => {
    try {
      const errSummary = error ? error.status || JSON.stringify(error) : null;
      const len = Array.isArray(leaveDataResponse)
        ? leaveDataResponse.length
        : leaveDataResponse
          ? 1
          : 0;
    } catch (e) {}

    if (isError) {
      console.error("Error fetching leaves for supervisor:", error);
    }
  }, [isLoading, isError, error, leaveDataResponse]);

  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedLeaveType, setSelectedLeaveType] = useState("All");
  const [selectedDaysFilter, setSelectedDaysFilter] = useState("Default");
  // Date range state (default to last 1 week)
  const getTodayInThimphu = () => {
    const now = new Date();
    const thimphuDate = new Date(
      now.toLocaleString("en-US", { timeZone: "Asia/Thimphu" }),
    );
    thimphuDate.setHours(0, 0, 0, 0);
    return thimphuDate;
  };

  const today = getTodayInThimphu();
  const oneWeekAgo = new Date(today);
  oneWeekAgo.setDate(today.getDate() - 6); // 7 days including today

  // Helper function to format date as YYYY-MM-DD without timezone issues
  const formatDateString = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // By default show all records (no date filtering). User can pick a range if needed.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const dateRangeButtonRef = useRef(null);

  // Status options for CustomSelect
  const statusOptions = [
    { value: "All", label: "All" },
    { value: "Approved", label: "Approved" },
    { value: "Pending", label: "Pending" },
    { value: "Rejected", label: "Rejected" },
    { value: "Forwarded", label: "Forwarded" },
  ];

  // Leave type options for CustomSelect
  const leaveTypeOptions = [
    { value: "All", label: "All" },
    { value: "Casual Leave", label: "Casual Leave" },
    { value: "Earned Leave", label: "Earned Leave" },
    { value: "Manual Leave", label: "Manual Leave" },
  ];

  // Days filter options for CustomSelect
  const daysFilterOptions = [
    { value: "Default", label: "Default" },
    { value: "1", label: "1 Day" },
    { value: "2-3", label: "2-3 Days" },
    { value: "4-5", label: "4-5 Days" },
    { value: "6+", label: "6+ Days" },
  ];

  // Helper function to format date for display
  const formatDate = (dateString, short = false) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (short) {
        // dd/mm/yyyy format for compact display
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [showLeaveDetails, setShowLeaveDetails] = useState(false);
  const [actionNote, setActionNote] = useState("");
  const [noteError, setNoteError] = useState("");
  const [localLeaveData, setLocalLeaveData] = useState([]);

  // Enhanced note extraction with consistent date handling
  const extractSupervisorInfo = (leave) => {
    // First, check if supervisor info is directly in the leave object
    if (leave.supervisorEmail || leave.supervisorName) {
      const result = {
        note: leave.supervisorNote || "",
        email: leave.supervisorEmail || "",
        name: leave.supervisorName || "",
        role: "Supervisor",
        date:
          leave.supervisorNoteDate ||
          leave.supervisorUpdatedAt ||
          leave.forwardedAt ||
          leave.updatedAt ||
          leave.createdAt ||
          "",
      };
      return result;
    }

    // Check workflow notes array
    if (leave.notes && Array.isArray(leave.notes)) {
      const supervisorNotes = leave.notes
        .filter(
          (note) =>
            note.actorRole === "SUPERVISOR" ||
            note.actorRole === "Supervisor" ||
            (note.actorId && note.actorId === supervisorId),
        )
        .sort(
          (a, b) =>
            new Date(b.timestamp || b.createdAt || b.date) -
            new Date(a.timestamp || a.createdAt || a.date),
        );

      if (supervisorNotes.length > 0) {
        const latestNote = supervisorNotes[0];
        const result = {
          note: latestNote.message || latestNote.note || "",
          email: latestNote.actorEmail || latestNote.actorId || "",
          name: latestNote.actorName || "Supervisor",
          role: latestNote.actorRole || "Supervisor",
          date:
            latestNote.timestamp ||
            latestNote.createdAt ||
            latestNote.date ||
            latestNote.updatedAt ||
            "",
        };
        return result;
      }
    }

    // Check workflow states
    if (leave.workflowStates && Array.isArray(leave.workflowStates)) {
      const supervisorState = leave.workflowStates.find(
        (state) => state.role === "SUPERVISOR" || state.role === "Supervisor",
      );

      if (supervisorState) {
        const result = {
          note: supervisorState.note || "",
          email: supervisorState.actorEmail || "",
          name: supervisorState.actorName || "Supervisor",
          role: supervisorState.role || "Supervisor",
          date:
            supervisorState.timestamp ||
            supervisorState.updatedAt ||
            supervisorState.createdAt ||
            "",
        };

        return result;
      }
    }

    return { note: "", email: "", name: "", role: "Supervisor", date: "" };
  };

  // Enhanced manager info extraction with consistent date handling
  const extractManagerInfo = (leave) => {
    // Check if manager info is directly in the leave object
    if (leave.managerEmail || leave.managerName) {
      const result = {
        note: leave.managerNote || "",
        email: leave.managerEmail || "",
        name: leave.managerName || "",
        role: "Manager",
        date:
          leave.managerNoteDate ||
          leave.managerUpdatedAt ||
          leave.decisionAt ||
          leave.updatedAt ||
          leave.createdAt ||
          "",
      };
      return result;
    }

    // Check workflow notes array for manager
    if (leave.notes && Array.isArray(leave.notes)) {
      const managerNotes = leave.notes
        .filter(
          (note) =>
            note.actorRole === "MANAGER" || note.actorRole === "Manager",
        )
        .sort(
          (a, b) =>
            new Date(b.timestamp || b.createdAt || b.date) -
            new Date(a.timestamp || a.createdAt || a.date),
        );

      if (managerNotes.length > 0) {
        const latestNote = managerNotes[0];
        const result = {
          note: latestNote.message || latestNote.note || "",
          email: latestNote.actorEmail || latestNote.actorId || "",
          name: latestNote.actorName || "Manager",
          role: latestNote.actorRole || "Manager",
          date:
            latestNote.timestamp ||
            latestNote.createdAt ||
            latestNote.date ||
            latestNote.updatedAt ||
            "",
        };
        return result;
      }
    }

    // Check workflow states for manager
    if (leave.workflowStates && Array.isArray(leave.workflowStates)) {
      const managerState = leave.workflowStates.find(
        (state) => state.role === "MANAGER" || state.role === "Manager",
      );

      if (managerState) {
        const result = {
          note: managerState.note || "",
          email: managerState.actorEmail || "",
          name: managerState.actorName || "Manager",
          role: managerState.role || "Manager",
          date:
            managerState.timestamp ||
            managerState.updatedAt ||
            managerState.createdAt ||
            "",
        };
        return result;
      }
    }
    return { note: "", email: "", name: "", role: "Manager", date: "" };
  };

  // Update local data when API data changes - handle multiple response shapes
  useEffect(() => {
    if (!leaveDataResponse) return;

    let raw = leaveDataResponse;

    // Handle common wrapper shapes
    if (Array.isArray(leaveDataResponse)) {
      raw = leaveDataResponse;
    } else if (
      leaveDataResponse.data &&
      Array.isArray(leaveDataResponse.data)
    ) {
      raw = leaveDataResponse.data;
    } else if (
      leaveDataResponse.leaves &&
      Array.isArray(leaveDataResponse.leaves)
    ) {
      raw = leaveDataResponse.leaves;
    } else if (
      leaveDataResponse.content &&
      Array.isArray(leaveDataResponse.content)
    ) {
      raw = leaveDataResponse.content;
    } else if (
      leaveDataResponse.results &&
      Array.isArray(leaveDataResponse.results)
    ) {
      raw = leaveDataResponse.results;
    } else if (typeof leaveDataResponse === "object") {
      // Try to find the first array property as a fallback
      const arrProp = Object.keys(leaveDataResponse).find((k) =>
        Array.isArray(leaveDataResponse[k]),
      );
      if (arrProp) raw = leaveDataResponse[arrProp];
    }

    if (Array.isArray(raw)) {
      setLocalLeaveData(transformLeaveData(raw));
    } else {
      // If no array found, clear local data and log for debugging
      console.warn(
        "SupervisorEmployeeLeaveRequests: unexpected leaveDataResponse shape",
        leaveDataResponse,
      );
      setLocalLeaveData([]);
    }
  }, [leaveDataResponse]);

  const transformLeaveData = (data) => {
    if (!data) return [];

    // Sort the data by submitted date in descending order (newest first)
    const sortedData = [...data].sort((a, b) => {
      const dateA = new Date(a.submittedAt || a.createdAt || a.startDate);
      const dateB = new Date(b.submittedAt || b.createdAt || b.startDate);

      // Handle invalid dates
      if (isNaN(dateA.getTime())) return 1;
      if (isNaN(dateB.getTime())) return -1;

      return dateB.getTime() - dateA.getTime(); // Descending order
    });

    return sortedData.map((leave) => {
      // Extract supervisor info - check multiple possible sources
      const supervisorInfo = extractSupervisorInfo(leave);
      const managerInfo = extractManagerInfo(leave);

      // Ensure user field is always populated for supervisor view
      const userName =
        leave.Email ||
        leave.user?.name ||
        leave.user?.Email ||
        leave.employee ||
        "Unknown User";

      // Calculate days and determine if it's a single day leave
      const days = calculateLeaveDays(leave);
      const isSingleDay =
        days <= 1 ||
        leave.duration === "FULL_DAY" ||
        leave.duration === "HALF_DAY";

      // Determine if it's a manual leave
      const isManualLeave =
        leave.leaveTypeLabel !== "CASUAL" && leave.leaveTypeLabel !== "EARN";

      return {
        id: leave.id || leave._id || leave.leaveId || leave.leave_id,
        user: userName,
        position: leave.user?.position || "Technician",
        type: getLeaveTypeDisplay(leave.leaveTypeLabel), // This will show "Manual Leave" for non-CASUAL/EARN
        reason: leave.reason || "No reason provided",
        days: leave.totalDays,
        startDate: leave.startDate,
        // Set endDate to empty string for single day leaves
        endDate: isSingleDay ? "" : leave.endDate,
        submittedDate: leave.submittedAt || leave.createdAt,
        status: getStatusFromBackend(leave.status),

        // Supervisor information - ALWAYS use current supervisor's info
        supervisorNote: leave.supervisorNote || supervisorInfo.note || "",
        supervisorName: supervisorName, // Always use current supervisor's name
        supervisorEmail: supervisorEmail, // Always use current supervisor's email
        supervisorRole: "Supervisor",
        supervisorNoteDate:
          supervisorInfo.date ||
          leave.supervisorNoteDate ||
          leave.forwardedAt ||
          "", // FIXED: Use extracted date

        // Manager information
        managerNote: leave.managerNote || managerInfo.note || "",
        managerName: managerInfo.name || "Manager",
        managerEmail: managerInfo.email || "",
        managerRole: managerInfo.role || "Manager",
        managerNoteDate:
          managerInfo.date || leave.managerNoteDate || leave.decisionAt || "", // FIXED: Use extracted date

        // Additional leave details
        isHalfDay: leave.duration === "HALF_DAY",
        duration: leave.duration,
        halfDaySession: leave.halfDaySession,
        isSingleDay: isSingleDay,
        isManualLeave: isManualLeave, // Add this flag for manual leaves
        originalLeaveType: leave.leaveTypeLabel, // Keep original for display in overlay

        // Backend fields for actions
        _rawLeave: leave,
      };
    });
  };

  // Calculate actual leave days from backend data - SUPERVISOR VERSION
  const calculateLeaveDays = (leave) => {
    if (!leave.startDate) return 0;

    switch (leave.duration) {
      case "HALF_DAY":
        return 0.5;
      case "FULL_DAY":
        return 1;
      case "MULTI_DAY":
        if (leave.endDate) {
          const start = new Date(leave.startDate);
          const end = new Date(leave.endDate);
          const diffTime = Math.abs(end - start);
          return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        }
        return 1;
      default:
        return 1;
    }
  };

  // Map backend leave type to frontend display - MODIFIED
  const getLeaveTypeDisplay = (backendLeaveType) => {
    const typeMap = {
      EARN: "Earned Leave",
      CASUAL: "Casual Leave",
    };

    // If it's not CASUAL or EARN, show as Manual Leave
    return typeMap[backendLeaveType] || "Manual Leave";
  };

  // Map backend status to frontend status
  const getStatusFromBackend = (backendStatus) => {
    const statusMap = {
      PENDING: "Pending",
      APPROVED: "Approved",
      REJECTED: "Rejected",
      FORWARDED: "Forwarded",
    };
    return statusMap[backendStatus] || backendStatus || "Pending";
  };

  // Get transformed leave data
  const leaveData = localLeaveData;

  const handleBack = () => {
    navigate("/supervisor/attendance");
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800";
      case "Pending":
        return "bg-yellow-100 text-yellow-800";
      case "Rejected":
        return "bg-red-100 text-red-800";
      case "Forwarded":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // FIXED: Enhanced date-time formatting with better error handling
  const formatDateTime = (dateString) => {
    if (!dateString) return "No date available";

    try {
      let date;

      // Handle different date formats
      if (typeof dateString === "string") {
        if (dateString.includes("T")) {
          // ISO format
          date = new Date(dateString);
        } else if (dateString.includes(",")) {
          // Format like "Nov 27, 2025, 02:06 AM"
          date = new Date(dateString.replace(/, /g, " "));
        } else {
          // Fallback
          date = new Date(dateString);
        }
      } else if (dateString instanceof Date) {
        date = dateString;
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) {
        return "Invalid date";
      }

      // Add 6 hours for timezone adjustment
      date.setHours(date.getHours() + 6);

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch (error) {
      return "Date format error";
    }
  };

  const formatDateForDisplay = (leave, field) => {
    if (field === "endDate") {
      // Show dash for single day leaves (including half-day)
      if (
        leave.isSingleDay ||
        !leave.endDate ||
        leave.startDate === leave.endDate
      ) {
        return "-";
      }
    }
    return formatDate(leave[field]);
  };

  const handleRowClick = (leave) => {
    setSelectedLeave(leave);
    setShowLeaveDetails(true);
    setActionNote(leave.supervisorNote || "");
    setNoteError("");
  };

  const handleCloseDetails = () => {
    setShowLeaveDetails(false);
    setSelectedLeave(null);
    setActionNote("");
    setNoteError("");
  };

  const validateNote = () => {
    if (!actionNote.trim()) {
      setNoteError("Please provide a note for your action");
      return false;
    }
    setNoteError("");
    return true;
  };

  // ✅ FIXED: Enhanced reject handler with correct API field names
  const handleReject = async () => {
    try {
      if (!actionNote.trim()) {
        toast.error(
          "Rejection Reason Required: Please provide a reason for rejection.",
        );
        return;
      }

      await rejectLeave({
        leaveId: selectedLeave.id,
        supervisorId: supervisorId,
        supervisorEmail: supervisorEmail,
        note: actionNote,
      }).unwrap();

      toast.success(
        "Leave Request Rejected: The leave request has been successfully rejected.",
      );
      handleCloseDetails();
      refetch();
    } catch (error) {
      console.error("Failed to reject leave:", error);
      toast.error(
        `Failed to Reject Leave: ${error?.data?.error || error?.error || "Unknown error occurred"}`,
      );
    }
  };

  // ✅ FIXED: Enhanced forward handler with correct API field names
  const handleForward = async () => {
    try {
      if (!actionNote.trim()) {
        toast.error("Note Required: Please provide a note before forwarding.");
        return;
      }

      // Get manager info from supervisor's userInfo
      const managerId = userInfo?.managerInfo?.id || userInfo?.managerId;
      const managerEmail =
        userInfo?.managerInfo?.email || userInfo?.managerEmail;

      // Debug: log what we have to identify correct ID field
      console.log("DEBUG selectedLeave:", selectedLeave);
      console.log("DEBUG _rawLeave:", selectedLeave._rawLeave);
      console.log("DEBUG leaveId to use:", selectedLeave.id);
      console.log("DEBUG managerId:", managerId, "managerEmail:", managerEmail);

      await forwardLeave({
        leaveId: selectedLeave.id,
        supervisorId: supervisorId,
        supervisorEmail: supervisorEmail,
        managerId: managerId,
        managerEmail: managerEmail,
        note: actionNote,
      }).unwrap();

      toast.success(
        "Leave Request Forwarded: The leave request has been successfully forwarded to the manager.",
      );
      handleCloseDetails();
      refetch();
    } catch (error) {
      console.error("Failed to forward leave:", error);
      toast.error(
        `Failed to Forward Leave: ${error?.data?.error || error?.error || "Unknown error occurred"}`,
      );
    }
  };

  // Filter data based on all filters
  const filteredData = leaveData.filter((record) => {
    const statusMatch =
      selectedStatus === "All" || record.status === selectedStatus;
    const leaveTypeMatch =
      selectedLeaveType === "All" || record.type === selectedLeaveType;

    // Date filtering - handle both string dates (YYYY-MM-DD) and Date objects
    let dateMatch = true;
    if (startDate || endDate) {
      const recordStartDate = record.startDate;
      const recordEndDate = record.isSingleDay
        ? record.startDate
        : record.endDate;

      if (startDate && endDate) {
        // Both dates selected - record should fall within the range
        dateMatch = recordStartDate >= startDate && recordEndDate <= endDate;
      } else if (startDate) {
        // Only start date selected - record should start on or after startDate
        dateMatch = recordStartDate >= startDate;
      } else if (endDate) {
        // Only end date selected - record should end on or before endDate
        dateMatch = recordEndDate <= endDate;
      }
    }

    return statusMatch && leaveTypeMatch && dateMatch;
  });

  // Sort data based on days filter
  const sortedData = [...filteredData].sort((a, b) => {
    if (selectedDaysFilter === "Ascending") {
      return a.days - b.days;
    } else if (selectedDaysFilter === "Descending") {
      return b.days - a.days;
    }
    return 0;
  });

  // FIXED: Consistent note display logic
  const shouldShowSupervisorNote = (leave) => {
    return (
      leave.supervisorNote &&
      leave.supervisorNote.trim() !== "" &&
      (leave.supervisorName || leave.supervisorEmail)
    );
  };

  const shouldShowManagerNote = (leave) => {
    return (
      leave.managerNote &&
      leave.managerNote.trim() !== "" &&
      leave.managerName &&
      (leave.status === "Approved" ||
        leave.status === "Rejected" ||
        leave.status === "Forwarded")
    );
  };

  // Note: do not block rendering when `divisionId` is missing — the API can be called without it.

  // Loading state
  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading leave requests...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-red-600">
            Error loading leave requests. Please try again.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-10xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-1 mb-4 sm:mb-6">
        <button
          aria-label="Back"
          onClick={handleBack}
          className=" md:flex w-8 h-8 sm:w-9 sm:h-9 items-center justify-center rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all"
        >
          <ChevronLeft
            size={24}
            strokeWidth={2}
            className="text-gray-700 sm:w-6 sm:h-6"
          />
        </button>
        <div className="flex items-center space-x-2 sm:space-x-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">
            Employee Leave Requests
          </h1>
        </div>
      </div>

      {/* Records Section */}
      <div className="bg-white rounded-lg border">
        <div className="p-6 border-b">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-bold text-zinc-800">
                Leave Requests
              </h2>
              <p className="text-sm text-zinc-600 mt-1">
                Manage technician leave applications in your division
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            {/* Left Side - Multiple Filters */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full lg:w-auto">
              {/* Status Filter */}
              <div className="flex-1 sm:flex-none">
                <label className="block text-[#ABABAB] text-gray-600 mb-1">
                  Status
                </label>
                <CustomSelect
                  options={statusOptions}
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  placeholder="All"
                  minWidth={160}
                />
              </div>

              {/* Leave Type Filter */}
              <div className="flex-1 sm:flex-none">
                <label className="block text-[#ABABAB] text-gray-600 mb-1">
                  Leave Type
                </label>
                <CustomSelect
                  options={leaveTypeOptions}
                  value={selectedLeaveType}
                  onChange={setSelectedLeaveType}
                  placeholder="All"
                  minWidth={160}
                />
              </div>

              {/* Date Range Filter */}
              {/* <div className="flex-1 sm:flex-none">
              <label className="block text-[#ABABAB] text-gray-600 mb-1">Date Range</label>
              <div className="relative">
                <button
                  ref={dateRangeButtonRef}
                  onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                  className="bg-white border border-gray-300 rounded-lg px-3 py-2 sm:px-4 sm:py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap flex-shrink-0"
                >
                  {startDate && endDate ? (
                    <>
                      <span className="hidden lg:inline">
                        {formatDate(startDate)} - {formatDate(endDate)}
                      </span>
                      <span className="lg:hidden">
                        {formatDate(startDate, true)} -{" "}
                        {formatDate(endDate, true)}
                      </span>
                    </>
                  ) : (
                    "Select date range"
                  )}
                </button>
                <DateRangePicker
                  show={showDateRangePicker}
                  onClose={() => setShowDateRangePicker(false)}
                  startDate={startDate}
                  endDate={endDate}
                  onDateRangeSelect={({ start, end }) => {
                    if (start && end) {
                      setStartDate(start);
                      setEndDate(end);
                    }
                  }}
                  buttonRef={dateRangeButtonRef}
                  allowFutureDates={true}
                />
              </div>
            </div> */}
            </div>
          </div>
        </div>

        {/* Scrollable Body */}
        <div className="h-[500px] overflow-auto">
          <table className="w-full">
            <thead className="bg-zinc-50 border-b">
              <tr>
                <th className="px-4 sm:px-6 py-3 text-left text-[#] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  USER
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  TYPE
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  DAYS
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  SUBMITTED ON
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  FROM
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  TO
                </th>
                <th className="px-4 sm:px-6 py-3 text-left text-[#ABABAB] font-medium text-gray-500 tracking-wider sticky top-0 bg-gray-50 z-10">
                  STATUS
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-zinc-200">
              {sortedData.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-6 py-8 text-center text-zinc-500"
                  >
                    No leave requests found
                  </td>
                </tr>
              ) : (
                sortedData.map((record) => (
                  <tr
                    key={record.id}
                    className="hover:bg-zinc-50 cursor-pointer transition-colors"
                    onClick={() => handleRowClick(record)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-zinc-900">
                      <div>
                        <div>{record.user}</div>
                        <div className="text-sm text-zinc-600">
                          {record.position}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {record.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-900">
                      {record.days} {record.days === 1 ? "day" : "days"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {formatDate(record.submittedDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {formatDate(record.startDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-zinc-600">
                      {record.isSingleDay
                        ? formatDate(record.startDate)
                        : formatDate(record.endDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(record.status)}`}
                      >
                        {record.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Leave Details Overlay */}
      {showLeaveDetails && selectedLeave && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-2 sm:mx-4">
            <div className="p-4 sm:p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-4 sm:mb-6">
                <div className="space-y-2 sm:space-y-3">
                  {/* Status Badge */}
                  <div className="flex-shrink-0">
                    <span
                      className={`inline-flex px-2 sm:px-3 py-1 text-[#ABABAB] sm:text-sm font-semibold rounded-full ${getStatusColor(selectedLeave.status)}`}
                    >
                      {selectedLeave.status}
                    </span>
                  </div>

                  {/* Employee Profile */}
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm sm:text-base">
                        {selectedLeave.user.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                        {selectedLeave.user}
                      </h3>
                      <p className="text-[#ABABAB] sm:text-sm text-gray-600">
                        {selectedLeave.position}
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCloseDetails}
                  className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600" />
                </button>
              </div>

              {/* Leave Type */}
              <div className="mb-1">
                <h3 className="font-semibold text-gray-800 text-base sm:text-lg">
                  {selectedLeave.isManualLeave
                    ? selectedLeave.originalLeaveType
                    : selectedLeave.type}
                </h3>
              </div>

              {/* Reason */}
              <div className="mb-3 sm:mb-4">
                <h4 className="font-medium text-gray-700 mb-1 sm:mb-2 text-sm sm:text-base">
                  Reason:
                </h4>
                <p className="text-gray-700 bg-gray-50 p-3 sm:p-4 rounded-lg border border-gray-200 text-sm sm:text-base">
                  {selectedLeave.isHalfDay && (
                    <p className="text-sm text-blue-600 mt-1">
                      Half Day -{" "}
                      {selectedLeave.halfDaySession === "MORNING"
                        ? "Morning"
                        : "Afternoon"}{" "}
                      Session
                    </p>
                  )}
                  {selectedLeave.reason}
                </p>
              </div>

              {/* Dates and Days */}
              <div className="mb-3 sm:mb-4">
                <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                    Submitted On:
                  </span>
                  <span className="text-gray-900 text-sm sm:text-base">
                    {formatDateTime(selectedLeave.submittedDate)}
                  </span>
                </div>

                {/* Show "Leave Date" for single day leaves (including half-day) */}
                {selectedLeave.isSingleDay ? (
                  <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                    <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                      Leave Date:
                    </span>
                    <span className="text-gray-900 text-sm sm:text-base">
                      {formatDate(selectedLeave.startDate)}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                      <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                        From:
                      </span>
                      <span className="text-gray-900 text-sm sm:text-base">
                        {formatDate(selectedLeave.startDate)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-gray-300 pb-2 mb-3 sm:mb-4">
                      <span className="text-xs sm:text-sm font-medium text-gray-700">
                        To:
                      </span>
                      <span className="text-gray-900 text-sm sm:text-base">
                        {formatDate(selectedLeave.endDate)}
                      </span>
                    </div>
                  </>
                )}

                <div className="flex justify-between items-center border-b border-gray-300 pb-2">
                  <span className="text-[#ABABAB] sm:text-sm font-medium text-gray-700">
                    Days
                  </span>
                  <span className="text-gray-900 text-sm sm:text-base">
                    {selectedLeave.days}{" "}
                    {selectedLeave.days === 1 ? "Day" : "Days"}
                  </span>
                </div>
              </div>

              {/* Action Buttons for Pending Status */}
              {selectedLeave.status === "Pending" && (
                <div className="space-y-4">
                  {/* Note Input with Validation */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Note for why you rejected or forwarded this request
                    </label>
                    <textarea
                      value={actionNote}
                      onChange={(e) => {
                        setActionNote(e.target.value);
                        if (noteError && e.target.value.trim()) {
                          setNoteError("");
                        }
                      }}
                      rows={3}
                      placeholder="Enter your note here..."
                      className={`w-full border-2 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base ${
                        noteError ? "border-red-500" : "border-gray-300"
                      }`}
                    />
                    {noteError && (
                      <p className="text-red-500 text-[#ABABAB] mt-1 flex items-center">
                        <span className="mr-1">⚠</span>
                        {noteError}
                      </p>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-3 justify-center">
                    <button
                      onClick={handleReject}
                      className="px-8 bg-red-600 text-white py-3 rounded-lg font-semibold hover:bg-red-700 transition-colors text-sm sm:text-base whitespace-nowrap"
                    >
                      Reject
                    </button>
                    <button
                      onClick={handleForward}
                      className="px-8 bg-black text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors text-sm sm:text-base whitespace-nowrap"
                    >
                      Forward
                    </button>
                  </div>
                </div>
              )}

              {/* ✅ FIXED: Enhanced Notes Display Section with Consistent Dates */}
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                {/* Only show manual leave message for manual leaves */}
                {selectedLeave.isManualLeave && (
                  <div className="text-center">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <h4 className="font-semibold text-green-800 text-sm sm:text-base mb-2">
                        Manual Leave Approved
                      </h4>
                      <p className="text-green-700 text-sm">
                        Manual leaves are automatically approved and do not
                        require supervisor or manager review.
                      </p>
                    </div>
                  </div>
                )}

                {/* Supervisor Note - Show for ALL statuses when supervisor has taken action */}
                {shouldShowSupervisorNote(selectedLeave) && (
                  <div className="mb-4 last:mb-0">
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold text-xs sm:text-sm">
                          {selectedLeave.supervisorName?.charAt(0) ||
                            selectedLeave.supervisorEmail?.charAt(0) ||
                            "S"}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                          {selectedLeave.supervisorEmail ||
                            selectedLeave.supervisorName ||
                            "Supervisor"}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600">
                          Supervisor
                        </p>
                      </div>
                    </div>
                    <div className="p-1">
                      <p className="text-gray-700 text-sm sm:text-base mb-2">
                        {selectedLeave.supervisorNote}
                      </p>
                      {/* ✅ ALWAYS show timestamp if available - for both reject and forward actions */}
                      {selectedLeave.supervisorNoteDate && (
                        <p className="text-xs text-gray-500">
                          Note added on:{" "}
                          {formatDateTime(selectedLeave.supervisorNoteDate)}
                        </p>
                      )}
                    </div>
                    {selectedLeave.managerNote && (
                      <div className="border-b border-gray-300 my-4"></div>
                    )}
                  </div>
                )}

                {/* Manager Note - Show for Approved, Rejected, and Forwarded status */}
                {shouldShowManagerNote(selectedLeave) && (
                  <div className="mb-4 last:mb-0">
                    <div className="flex items-center space-x-2 sm:space-x-3 mb-2 sm:mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 font-semibold text-xs sm:text-sm">
                          {selectedLeave.managerName?.charAt(0) || "M"}
                        </span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 text-sm sm:text-base">
                          {selectedLeave.managerEmail ||
                            selectedLeave.managerName ||
                            "Manager"}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600">
                          Manager
                        </p>
                      </div>
                    </div>
                    <div className="p-1">
                      <p className="text-gray-700 text-sm sm:text-base mb-2">
                        {selectedLeave.managerNote}
                      </p>
                      {/* ✅ ALWAYS show timestamp if available */}
                      {selectedLeave.managerNoteDate && (
                        <p className="text-xs text-gray-500">
                          Note added on:{" "}
                          {formatDateTime(selectedLeave.managerNoteDate)}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Show message if no notes exist */}
                {!selectedLeave.supervisorNote &&
                  !selectedLeave.managerNote &&
                  !selectedLeave.isManualLeave && (
                    <div className="text-center py-4">
                      <p className="text-gray-500 text-sm">
                        No review notes available for this leave request.
                      </p>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
